import {
  LayoutDashboard, FolderKanban, Archive, BarChart3,
  FlaskConical, Lightbulb, Sparkles, UserCog, Building2, FileSpreadsheet, FolderTree,
  FileSignature, Sun, HardHat,
} from 'lucide-react';
import { REPORTS, reportPath } from './reportList';

/* The sidebar, in the order it is shown.

   `adminOnly` means the Construction Manager alone. Properties and User
   Management refuse anyone else on the page itself, so hiding the link only
   avoids a dead end. System Test and Suggestions do not refuse anyone yet, so
   for those two this list is presentation, not enforcement -- a typed URL
   still reaches them.

   Submitting a suggestion is a different thing and is not gated here: that is
   the "Suggest an Improvement" button in the sidebar footer, open to every
   tier, which is what the Suggestion entity's readOnlyMayCreate rule exists
   for. Hiding the Suggestions page hides triage, not submission. */
export const navItems = [
  /* Where everyone lands: what needs them today, across every project. */
  { label: 'My Day', path: '/MyDay', icon: Sun },
  { label: 'Dashboard', path: '/Dashboard', icon: LayoutDashboard },
  { label: 'Projects', path: '/Projects', icon: FolderKanban },
  /* A dropdown: the reports are listed under it, each its own link.
     See @/lib/reportList. */
  {
    label: 'Reports', path: '/Reports', icon: BarChart3,
    children: REPORTS.map(r => ({ key: r.key, label: r.label, path: reportPath(r.key), icon: r.icon })),
  },
  { label: 'Archived', path: '/ArchivedProjects', icon: Archive },
  { label: 'System Test', path: '/SystemTest', icon: FlaskConical, adminOnly: true },
  { label: 'Suggestions', path: '/Suggestions', icon: Lightbulb, adminOnly: true },
  { label: 'AI Update Assistant', path: '/TaskUpdates', icon: Sparkles },
  /* Not adminOnly: a Property Manager posting updates to their own projects
     from a spreadsheet is the whole reason this page exists. `writersOnly`
     keeps it away from the one tier that could never use it - the page refuses
     a viewer anyway, so hiding the link only avoids a dead end. The API
     refuses any project the caller may not write to, row by row. */
  { label: 'Bulk Updates', path: '/BulkUpdates', icon: FileSpreadsheet, writersOnly: true },
  /* Every tier: the contractors and their insurance are read by everyone, and
     kept by everyone who runs projects (migration 027). A viewer sees the
     page without its buttons, which is all the API would allow them. */
  { label: 'Contractors', path: '/Contractors', icon: HardHat },
  { label: 'Properties', path: '/PropertyManagement', icon: Building2, adminOnly: true },
  { label: 'Document Types', path: '/DocumentTypes', icon: FolderTree, adminOnly: true },
  { label: 'Lien Waiver Forms', path: '/LienWaiverForms', icon: FileSignature, adminOnly: true },
  { label: 'User Management', path: '/UserManagement', icon: UserCog, adminOnly: true },
];

/* Every tier that edits something. The VP of Property Management (migration
   026) is one: they post updates to the projects where they are the Property
   Manager, and the API refuses the rest row by row. */
const WRITER_ROLES = ['admin', 'project_manager', 'vp_property', 'user'];

/* The menu the given role may see. `role` is app_user.role, and an absent one
   -- the moment before the current user has loaded -- is treated as the least
   privileged, so an admin-only link is never shown and then taken away. */
export function visibleNavItems(role) {
  return navItems.filter(item => {
    if (item.adminOnly && role !== 'admin') return false;
    /* `viewer` is the read-only tier - see the note on Bulk Updates. An absent
       role is treated as least privileged here too, so the link does not flash
       on screen and then vanish once the user loads. */
    if (item.writersOnly && !WRITER_ROLES.includes(role)) return false;
    return true;
  });
}
